import React, { ReactElement, ReactNode } from 'react'
import ReactTooltip from 'react-tooltip'

import cn from 'classnames'
import type * as CSS from 'csstype'

import buttonConfiguration, { buttonSize, buttonValidation } from './buttonConfig'
import './coreButton.scss'

interface IConfig {
  buttonClass: string
  collapsedClass: string
  loadButtonClass: string
}

interface IConfigurationOptions {
  variation: buttonValidation
  size?: buttonSize
}

const classButtonConfig = ({ variation, size }: IConfigurationOptions): IConfig => {
  return buttonConfiguration[variation][size] as IConfig
}

interface IPrimaryButtonProps {
  text?: string | ReactNode
  variation?: buttonValidation
  size?: buttonSize
  disabled?: boolean
  onClick?: (e?: any) => void | Promise<void>
  collapsed?: boolean
  LeftIcon?: ReactElement
  RightIcon?: ReactElement
  CollapsedIcon?: ReactElement
  isLoading?: boolean
  fullWidth?: boolean
  width?: string
  style?: CSS.Properties
  className?: string
  type?: 'button' | 'submit' | 'reset'
  tooltip?: string
  ['data-test']?: string
  fitContent?: boolean
}

const sizeConfig = {
  small: '3px',
  normal: '7px',
  large: '9px'
}

interface ILoadingButton {
  loadButtonClass: string
  size?: buttonSize
  variation: buttonValidation
  style: CSS.Properties
  collapsed?: boolean
}

export const LoadingButton = ({ loadButtonClass, size, variation, style, collapsed, ...props }: ILoadingButton) => {
  return (
    <button className={loadButtonClass} style={style} {...props}>
      <div className='loading-button'>
        <span style={{ margin: sizeConfig[size] }} className='dot1'></span>
        <span style={{ margin: sizeConfig[size] }} className='dot2'></span>
        <span style={{ margin: sizeConfig[size] }} className='dot3'></span>
        {!collapsed && <span style={{ margin: sizeConfig[size] }} className='dot4'></span>}
      </div>
    </button>
  )
}

export const CoreButton = ({
  text,
  disabled,
  onClick,
  collapsed,
  isLoading,
  LeftIcon,
  RightIcon,
  CollapsedIcon,
  variation = 'primary',
  size = 'small',
  fullWidth,
  width,
  style,
  className,
  tooltip,
  fitContent,
  type = 'button',
  ...props
}: IPrimaryButtonProps) => {
  const config: IConfig = classButtonConfig({ variation, size, ...props })

  if (isLoading) {
    return (
      <LoadingButton
        loadButtonClass={config.loadButtonClass}
        size={size}
        collapsed={collapsed}
        variation={variation}
        style={{ ...style, width: fullWidth ? '100%' : fitContent ? 'fit-content' : width }}
        {...props}
      />
    )
  }

  return !collapsed ? (
    <button
      data-test='core-button-base'
      onClick={onClick}
      style={{ ...style, width: fullWidth ? '100%' : fitContent ? 'fit-content' : width }}
      disabled={disabled}
      {...props}
      className={cn(className, config.buttonClass)}>
      {LeftIcon}
      {text && (
        <>
          {React.isValidElement(text) ? (
            text
          ) : (
            <span data-test={`core-button-text-${text}`} className='body-medium'>
              {text}
            </span>
          )}
        </>
      )}
      {RightIcon}
    </button>
  ) : (
    <button
      data-test='core-button-base'
      onClick={onClick}
      style={{ ...style }}
      disabled={disabled}
      data-for={`custom-${tooltip}`}
      type={type}
      data-tip={tooltip}
      className={cn(className, config.collapsedClass)}>
      {CollapsedIcon || <span className='icon'>+</span>}
      <ReactTooltip
        delayShow={200}
        id={`custom-${tooltip}`}
        className='custom-tool-tip-component'
        textColor='black'
        backgroundColor='white'
        effect='solid'
      />
    </button>
  )
}

export default CoreButton
